import React, { useState, useEffect } from 'react';
import SuperAdminUserTable from './SuperAdminUserTable';
import UserFormModal from './UserFormModal';
import AddChildCompanyModal from './AddChildCompanyModal';
import AddDepartmentModal from './AddDepartmentModal';
import AddCompanyModal from './AddCompanyModal';
import { 
  // fetchAllUsers, 
  fetchAllCompanies, fetchAllDepartments } from '../../api/packageService';
import { useNavigate, useLocation } from 'react-router-dom';

// interface UserData {
//   _id: string;
//   email: string;
//   name: string;
//   usertypeId: string;
//   usertype: string;
//   phone: string;
//   __typename: string;
//   token: string;
// }

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  phone: string;
  usertype: string;
  companyId?: string;
  companyName?: string;
  childCompanyName?: string;
  city?: string;
  state?: string;
  country?: string;
  gender?: string;
}

// Define interface for company data
interface Company {
  _id: string;
  companyName: string;
  isParent?: boolean;
}

// Define interface for company details response
// interface CompanyDetails {
//   parentCompany: {
//     _id: string;
//     companyName: string;
//   };
//   childCompanies: Array<{
//     _id: string;
//     companyName: string;
//   }>;
// }

// Define interface for the user creation/editing form
interface UserForm {
  name: string;
  email: string;
  phone: string;
  password?: string;
  companyId?: string;
  usertype: string;
  childCompanyId?: string;
  departmentId?: string;
  city?: string;
  state?: string;
  country?: string;
  gender?: string;
}



interface UserManagementProps {
  userRole: 'SUPER_ADMIN' | 'ADMIN' | 'COMPANY_OWNER' | 'USER';
}

const UserManagement: React.FC<UserManagementProps> = ({ userRole }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setSelectedDepartmentId] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('All');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddChildCompanyModal, setShowAddChildCompanyModal] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [parentCompanyData, setParentCompanyData] = useState<any>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [userForm, setUserForm] = useState<UserForm>({
    name: '',
    email: '',
    phone: '',
    password: '',
    companyId: '',
    usertype: 'user',
    childCompanyId: '',
    departmentId: '',
    city: '',
    state: '',
    country: '',
    gender: ''
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [childCompanyFormLoading, setChildCompanyFormLoading] = useState(false);

  // Department form states
  const [departmentFormLoading, setDepartmentFormLoading] = useState(false);

  // Department state for user form
  const [departments, setDepartments] = useState<any[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        // Get token from localStorage
        const token = localStorage.getItem('AUTH_TOKEN');
        if (!token) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        const storedUserData = localStorage.getItem('user');
        let userData: any = null;
        if (storedUserData) {
          try {
            userData = JSON.parse(storedUserData);
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }

        if (!userData) {
          // Try to get user data from other possible locations
          const userStr = localStorage.getItem('user');
          if (userStr) {
            try {
              userData = JSON.parse(userStr);
            } catch (e) {
              console.error('Error parsing user data:', e);
            }
          }
        }

        let response;
        // Check if we're in a department view by looking at the current path
        const pathParts = location.pathname.split('/');
        const departmentId = pathParts[pathParts.length - 1];
        
        // Check if the current path is for a specific department
        if (location.pathname.includes('/department/') && departmentId && departmentId !== 'department') {
          // Fetch users by department
          const { fetchUsersByDepartment } = await import('../../api/packageService');
          response = await fetchUsersByDepartment(departmentId);
          setSelectedDepartmentId(departmentId);
        } else {
          // Fetch all users
          // response = await fetchAllUsers();
          setSelectedDepartmentId(null);
        }

        if (response.users) {
          // Transform the data to match our UserTable interface
          const transformedUsers = response.users.map((user: any) => {
            const userType = user.usertype || 'user';

            return {
              _id: user._id,
              name: user.name,
              email: user.email,
              phone: user.phone,
              usertype: userType,
              companyId: typeof user.companyId === 'object' ? user.companyId._id : user.companyId || '',
              companyName: user.companyId && typeof user.companyId === 'object' ? user.companyId.companyName : user.companyName || 'N/A',
              childCompanyName: user.childCompanyName || '',
              city: user.city || '',
              state: user.state || '',
              country: user.country || '',
              gender: user.gender || ''
            };
          });

          setUsers(transformedUsers);
          setFilteredUsers(transformedUsers);
        } else {
          setError('Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Error fetching users: ' + (err instanceof Error ? err.message : 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [location.pathname]);

  // Filter users based on search term and user type
  useEffect(() => {
    let result = [...users];

    // Apply search filter across all fields
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        (user.companyName && user.companyName.toLowerCase().includes(term)) ||
        user.phone.includes(term) ||
        (user.usertype && user.usertype.toLowerCase().includes(term))
      );
    }

    // Apply user type filter
    if (userTypeFilter !== 'All') {
      result = result.filter(user => user.usertype === userTypeFilter);
    }

    setFilteredUsers(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, userTypeFilter, users]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  // Fetch companies and departments when either modal opens
  useEffect(() => {
    const fetchData = async () => {
      if ((showCreateModal || showEditModal || showAddDepartmentModal || showAddCompanyModal)) {
        setLoadingCompanies(true);
        setLoadingDepartments(true);
        try {
          // Get token from localStorage
          const token = localStorage.getItem('AUTH_TOKEN');
          if (!token) {
            setError('User not authenticated');
            setLoadingCompanies(false);
            setLoadingDepartments(false);
            return;
          }

          const storedUserData = localStorage.getItem('user');
          let userData: any = null;
          if (storedUserData) {
            try {
              userData = JSON.parse(storedUserData);
            } catch (e) {
              console.error('Error parsing user data:', e);
            }
          }

          if (!userData) {
            // Try to get user data from other possible locations
            const userStr = localStorage.getItem('user');
            if (userStr) {
              try {
                userData = JSON.parse(userStr);
              } catch (e) {
                console.error('Error parsing user data:', e);
              }
            }
          }

          // Fetch real companies data
          const companiesResponse = await fetchAllCompanies();
          if (companiesResponse && companiesResponse.data) {
            setCompanies(companiesResponse.data);
          }

          // Fetch real departments data
          const departmentsResponse = await fetchAllDepartments();
          if (departmentsResponse && departmentsResponse.data) {
            setDepartments(departmentsResponse.data);
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          setError('Error fetching data: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
          setLoadingCompanies(false);
          setLoadingDepartments(false);
        }
      }
    };

    fetchData();
  }, [showCreateModal, showEditModal, showAddDepartmentModal, showAddCompanyModal, userRole]);

  // Effect to set parent company data when AddChildCompanyModal is opened
  useEffect(() => {
    if (showAddChildCompanyModal && companies.length > 0) {
      // Find the parent company in the companies list
      const parentCompany = companies.find((company: any) =>
        company.companyName && company.companyName.includes('(Parent)')
      );

      if (parentCompany) {
        setParentCompanyData(parentCompany);
      }
    }
  }, [showAddChildCompanyModal, companies]);

  const handleEditUser = (id: string) => {
    // Find the user to edit
    const userToEdit = users.find(user => user._id === id);
    if (userToEdit) {
      // Populate the form with user data
      setUserForm({
        name: userToEdit.name,
        email: userToEdit.email,
        phone: userToEdit.phone,
        password: '', // Don't prefill password
        companyId: userToEdit.companyId || '',
        usertype: userToEdit.usertype || 'user',
        childCompanyId: '',
        departmentId: '', // Initialize departmentId (would need backend changes to populate actual value)
        city: userToEdit.city || '',
        state: userToEdit.state || '',
        country: userToEdit.country || '',
        gender: userToEdit.gender || ''
      });
      setEditingUserId(id);
      setShowEditModal(true);
    }
  };

  const handleDeleteUser = (id: string) => {
    setUserToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      // Get token from localStorage
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        setError('User not authenticated');
        return;
      }

      // const userData: any = JSON.parse(storedUserData);
      // const token = userData.token;

      // Show success modal
      setSuccessMessage('User deleted successfully!');
      setShowSuccessModal(true);

      // Remove user from the local state
      const updatedUsers = users.filter(user => user._id !== userToDelete);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);

      // Close confirm modal
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Error deleting user: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setShowDeleteConfirm(false);
    }
  };

  const handleExportToExcel = () => {
    // Create CSV content
    const headers = userRole === 'SUPER_ADMIN'
      ? ['Name', 'Email', 'Company', 'Child Company', 'Type', 'Phone']
      : ['Name', 'Email', 'Company', 'Child Company', 'Type', 'Phone'];

    const csvContent = [
      headers.join(','),
      ...filteredUsers.map(user =>
        `"${user.name}","${user.email}","${user.companyName || 'N/A'}","${user.childCompanyName || 'None'}","${user.usertype}","${user.phone || 'N/A'}"`
      )
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'users_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleUserTypeFilterChange = (userType: string) => {
    setUserTypeFilter(userType);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Special handling for phone input
    if (name === 'phone') {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 10) {
        setUserForm(prev => ({
          ...prev,
          [name]: numericValue
        }));
      }
      return;
    }

    setUserForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle create user form submission
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    // Validate phone number length
    if (userForm.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      setFormLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userForm.email)) {
      setError('Please enter a valid email address');
      setFormLoading(false);
      return;
    }

    // Validate password for new users
    if (!userForm.password) {
      setError('Password is required');
      setFormLoading(false);
      return;
    }

    try {
      // Get token from localStorage
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        setError('User not authenticated');
        setFormLoading(false);
        return;
      }

      // const userData: any = JSON.parse(storedUserData);
      // const token = userData.token;

      // Prepare data for user creation
      const userDataToSend: any = { ...userForm };

      // Handle company assignment based on user role
      if (userRole === 'SUPER_ADMIN') {
        // For Super Admin, companyId is used directly
        userDataToSend.companyId = userForm.companyId;
        delete userDataToSend.childCompanyId;
      } else {
        // For Admin and Company Owner, determine if it's a child company
        const selectedCompany = companies.find(company => company._id === userForm.companyId);

        if (selectedCompany && selectedCompany.companyName.includes('(Parent)')) {
          // It's a parent company - send companyId
          userDataToSend.companyId = userForm.companyId;
          delete userDataToSend.childCompanyId;
        } else {
          // It's a child company - send childCompanyId
          userDataToSend.childCompanyId = userForm.companyId;
          delete userDataToSend.companyId;
        }
      }

      // Simulate API call
      setTimeout(() => {
        // Close modal and reset form after a delay
        setShowCreateModal(false);
        setUserForm({
          name: '',
          email: '',
          phone: '',
          password: '',
          companyId: '',
          usertype: 'user',
          childCompanyId: '',
          departmentId: '',
          city: '',
          state: '',
          country: '',
          gender: ''
        });
        setFormLoading(false);
        
        // Add new user to the list (in a real implementation, this would come from the API response)
        // const newUser = {
        //   _id: `new-${Date.now()}`,
        //   name: userForm.name,
        //   email: userForm.email,
        //   phone: userForm.phone,
        //   usertype: userForm.usertype,
        //   companyId: userForm.companyId,
        //   companyName: companies.find(c => c._id === userForm.companyId)?.companyName || 'N/A',
        //   childCompanyName: '',
        //   city: userForm.city || '',
        //   state: userForm.state || '',
        //   country: userForm.country || '',
        //   gender: userForm.gender || ''
        // };
        
        const newUserData = {
          _id: `new-${Date.now()}`,
          name: userForm.name,
          email: userForm.email,
          phone: userForm.phone,
          usertype: userForm.usertype,
          companyId: userForm.companyId,
          companyName: companies.find(c => c._id === userForm.companyId)?.companyName || 'N/A',
          childCompanyName: '',
          city: userForm.city || '',
          state: userForm.state || '',
          country: userForm.country || '',
          gender: userForm.gender || ''
        };
        
        const updatedUsers = [...users, newUserData];
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
        
        setSuccessMessage('User created successfully!');
        setShowSuccessModal(true);
      }, 1500);
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Error creating user: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setFormLoading(false);
    }
  };

  // Handle edit user form submission
  const handleEditUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    // Validate phone number length
    if (userForm.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      setFormLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userForm.email)) {
      setError('Please enter a valid email address');
      setFormLoading(false);
      return;
    }

    if (!editingUserId) {
      setError('No user selected for editing');
      setFormLoading(false);
      return;
    }

    try {
      // Get token from localStorage
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        setError('User not authenticated');
        setFormLoading(false);
        return;
      }

      // const userData: any = JSON.parse(storedUserData);
      // const token = userData.token;

      // Prepare data for update
      const updateData: any = { ...userForm };
      if (!updateData.password) {
        delete updateData.password;
      }

      // Handle company assignment based on user role
      if (userRole === 'SUPER_ADMIN') {
        // For Super Admin, companyId is used directly
        updateData.companyId = userForm.companyId;
        delete updateData.childCompanyId;
      } else {
        // For Admin and Company Owner, determine if it's a child company
        const selectedCompany = companies.find(company => company._id === userForm.companyId);

        if (selectedCompany && selectedCompany.companyName.includes('(Parent)')) {
          // It's a parent company - send companyId
          updateData.companyId = userForm.companyId;
          delete updateData.childCompanyId;
        } else {
          // It's a child company - send childCompanyId
          updateData.childCompanyId = userForm.companyId;
          delete updateData.companyId;
        }
      }

      // Simulate API call
      setTimeout(() => {
        // Close modal and reset form after a delay
        setShowEditModal(false);
        setUserForm({
          name: '',
          email: '',
          phone: '',
          password: '',
          companyId: '',
          usertype: 'user',
          childCompanyId: '',
          departmentId: '',
          city: '',
          state: '',
          country: '',
          gender: ''
        });
        setEditingUserId(null);
        setFormLoading(false);
        
        // Update user in the list (in a real implementation, this would come from the API response)
        const updatedCompany = companies.find(company => company._id === userForm.companyId);
        
        const updatedUsers = users.map(user => 
          user._id === editingUserId 
            ? { 
                ...user, 
                name: userForm.name,
                email: userForm.email,
                phone: userForm.phone,
                usertype: userForm.usertype,
                companyId: userForm.companyId,
                companyName: updatedCompany?.companyName || user.companyName,
                city: userForm.city || user.city,
                state: userForm.state || user.state,
                country: userForm.country || user.country,
                gender: userForm.gender || user.gender
              } 
            : user
        );
        
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
        
        setSuccessMessage('User updated successfully!');
        setShowSuccessModal(true);
      }, 1500);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Error updating user: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setFormLoading(false);
    }
  };

  const handleCreateChildCompany = async (companyData: any) => {
    setChildCompanyFormLoading(true);

    try {
      // Get token from localStorage
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        setError('User not authenticated');
        setChildCompanyFormLoading(false);
        return;
      }

      // const userData: any = JSON.parse(storedUserData);
      // const token = userData.token;

      // Simulate API call
      setTimeout(() => {
        // Close modal after response
        setShowAddChildCompanyModal(false);
        setParentCompanyData(null);
        setChildCompanyFormLoading(false);

        // Add new child company to the companies list
        const newCompany = {
          _id: `child-${Date.now()}`,
          companyName: companyData.companyName,
          address: companyData.address
        };
        
        const updatedCompanies = [...companies, newCompany];
        setCompanies(updatedCompanies);

        setSuccessMessage('Child company created successfully!');
        setShowSuccessModal(true);
      }, 1500);
    } catch (err) {
      console.error('Error creating child company:', err);
      setError('Error creating child company: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setChildCompanyFormLoading(false);
    }
  };

  const handleCreateDepartment = async (departmentData: any) => {
    setDepartmentFormLoading(true);

    try {
      // Get token from localStorage
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        setError('User not authenticated');
        setDepartmentFormLoading(false);
        return;
      }

      // const userData: any = JSON.parse(storedUserData);
      // const token = userData.token;

      // Simulate API call
      setTimeout(() => {
        // Close modal after response
        setShowAddDepartmentModal(false);
        setDepartmentFormLoading(false);

        // Add new department to the departments list
        const newDepartment = {
          _id: `dept-${Date.now()}`,
          name: departmentData.name,
          companyId: departmentData.companyId
        };
        
        const updatedDepartments = [...departments, newDepartment];
        setDepartments(updatedDepartments);

        setSuccessMessage('Department created successfully!');
        setShowSuccessModal(true);
      }, 1500);
    } catch (err) {
      console.error('Error creating department:', err);
      setError('Error creating department: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setDepartmentFormLoading(false);
    }
  };

  const handleCreateCompany = async (companyData: any) => {
    setFormLoading(true);

    try {
      // Get token from localStorage
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        setError('User not authenticated');
        setFormLoading(false);
        return;
      }

      // const userData: any = JSON.parse(storedUserData);
      // const token = userData.token;

      // Simulate API call
      setTimeout(() => {
        // Close modal after response
        setShowAddCompanyModal(false);
        setFormLoading(false);

        // Add new company to the companies list
        const newCompany = {
          _id: `company-${Date.now()}`,
          companyName: companyData.companyName,
          address: companyData.address
        };
        
        const updatedCompanies = [...companies, newCompany];
        setCompanies(updatedCompanies);

        setSuccessMessage('Company created successfully!');
        setShowSuccessModal(true);
      }, 1500);
    } catch (err) {
      console.error('Error creating company:', err);
      setError('Error creating company: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setFormLoading(false);
    }
  };

  // Reset form when modals close
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setUserForm({
      name: '',
      email: '',
      phone: '',
      password: '',
      companyId: '',
      usertype: 'user',
      childCompanyId: '',
      departmentId: ''
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setUserForm({
      name: '',
      email: '',
      phone: '',
      password: '',
      companyId: '',
      usertype: 'user',
      childCompanyId: '',
      departmentId: ''
    });
    setEditingUserId(null);
  };

  // Determine current view based on URL path
  const currentView = location && location.pathname.includes('/companies') ? 'companies' :
                   location && location.pathname.includes('/departments') ? 'departments' :
                   location && location.pathname.includes('/add-user') ? 'add-user' :
                   'users';
  
  // Set appropriate title based on current view
  const getTitle = () => {
    switch(currentView) {
      case 'companies': return 'Companies Management';
      case 'departments': return 'Departments Management';
      case 'add-user': return 'Add New User';
      default: return 'User Management';
    }
  };
  
  // Render appropriate content based on current view
  const renderContent = () => {
    switch(currentView) {
      case 'companies':
        // For companies view, show companies list
        return (
          <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center">
            <h3 className="mb-3">Companies Management</h3>
            <p className="text-muted">Manage companies in the system</p>
            <button 
              className="btn btn-primary mx-auto"
              onClick={() => {
                // Show AddCompanyModal
                setShowAddCompanyModal(true);
              }}
            >
              Add New Company
            </button>
            <div className="mt-4 d-flex flex-wrap justify-content-center gap-2">
              <button 
                className="btn btn-outline-primary"
                onClick={() => navigate('/user-management')}
              >
                View Users
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/departments')}
              >
                View Departments
              </button>
            </div>
          </div>
        );
      case 'departments':
        // For departments view, show departments list
        return (
          <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center">
            <h3 className="mb-3">Departments Management</h3>
            <p className="text-muted">Manage departments in the system</p>
            <button 
              className="btn btn-primary mx-auto"
              onClick={() => {
                // Show AddDepartmentModal
                setShowAddDepartmentModal(true);
              }}
            >
              Add New Department
            </button>
            <div className="mt-4 d-flex flex-wrap justify-content-center gap-2">
              <button 
                className="btn btn-outline-primary"
                onClick={() => navigate('/user-management')}
              >
                View Users
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/companies')}
              >
                View Companies
              </button>
            </div>
          </div>
        );
      case 'add-user':
        // For add user view, show user creation form
        return (
          <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center">
            <h3 className="mb-3">Add New User</h3>
            <p className="text-muted">Create a new user account</p>
            <button 
              className="btn btn-primary mx-auto"
              onClick={() => {
                // Show Create User Modal
                setShowCreateModal(true);
              }}
            >
              Create User
            </button>
            <div className="mt-4 d-flex flex-wrap justify-content-center gap-2">
              <button 
                className="btn btn-outline-primary"
                onClick={() => navigate('/user-management')}
              >
                View Users
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/companies')}
              >
                View Companies
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/departments')}
              >
                View Departments
              </button>
            </div>
          </div>
        );
      default:
        // Default to users view
        return (
          <div className="flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
            <SuperAdminUserTable
              users={paginatedUsers}
              showActions={true}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onSearch={handleSearch}
              userTypeFilter={userTypeFilter}
              onUserTypeFilterChange={handleUserTypeFilterChange}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              totalUsers={filteredUsers.length}
              startIndex={startIndex}
              currentUserRole={userRole}
            />
          </div>
        );
    }
  };
  
  return (
    <div className="container-fluid px-1 px-md-2 pb-2" style={{ height: '100%', minHeight: 0 }}>
      <div className="row" style={{ height: '100%', margin: 0 }}>
        <div className="col-12 d-flex flex-column" style={{ height: '100%', minHeight: 0, padding: 0 }}>
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="card-header bg-white border-0 py-2">
              <div className="d-flex justify-content-between align-items-center flex-column flex-md-row text-start w-100">
                <div className="text-start mb-2 mb-md-0 w-100" style={{ textAlign: 'left' }}>
                  <h2 className="h5 fw-bold text-dark mb-0">{getTitle()}</h2>
                </div>
                <div className="d-flex flex-row gap-2 w-100 w-md-auto flex-wrap justify-content-end">
                  {currentView === 'users' && (
                    <button
                      className="btn btn-success rounded-pill px-2 px-md-2 py-1 py-md-2 flex-shrink-0"
                      onClick={handleExportToExcel}
                      title="Export to Excel"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: 'auto',
                        color: '#ffffff',
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                        border: 'none'
                      }}
                    >
                      <i className="bi bi-file-earmark-excel me-1 me-md-1" style={{ color: '#ffffff' }}></i>
                      <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Export to Excel</span>
                      <span className="d-sm-none" style={{ color: '#ffffff' }}>Export</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body p-0 flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
              <div className="p-1 p-md-2 flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
                {loading && currentView === 'users' ? (
                  <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading users...</p>
                  </div>
                ) : error && currentView === 'users' ? (
                  <div className="alert alert-danger m-4 flex-grow-1 d-flex flex-column justify-content-center">
                    {error}
                  </div>
                ) : (
                  renderContent()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UserFormModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateUser}
        title="Create New User"
        initialFormData={userForm}
        companies={companies}
        departments={departments}
        loadingCompanies={loadingCompanies}
        loadingDepartments={loadingDepartments}
        formLoading={formLoading}
        formError={error}
        formSuccess={successMessage}
        isEditMode={false}
        onFormChange={handleFormChange}
        userRole={userRole}
        onAddChildCompany={(parentCompany: any) => {
          setParentCompanyData(parentCompany);
          setShowAddChildCompanyModal(true);
        }}
      />

      <UserFormModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleEditUserSubmit}
        title="Edit User"
        initialFormData={userForm}
        companies={companies}
        departments={departments}
        loadingCompanies={loadingCompanies}
        loadingDepartments={loadingDepartments}
        formLoading={formLoading}
        formError={error}
        formSuccess={successMessage}
        isEditMode={true}
        onFormChange={handleFormChange}
        userRole={userRole}
        onAddChildCompany={(parentCompany: any) => {
          setParentCompanyData(parentCompany);
          setShowAddChildCompanyModal(true);
        }}
      />

      {/* Conditional modals based on user role */}
      {userRole === 'SUPER_ADMIN' && (
        <AddCompanyModal
          isOpen={showAddCompanyModal}
          onClose={() => {
            setShowAddCompanyModal(false);
          }}
          onSubmit={handleCreateCompany}
          formLoading={formLoading}
          formError={error}
          formSuccess={successMessage}
        />
      )}

      {(userRole === 'ADMIN' || userRole === 'COMPANY_OWNER') && (
        <>
          <AddChildCompanyModal
            isOpen={showAddChildCompanyModal}
            onClose={() => {
              setShowAddChildCompanyModal(false);
              setParentCompanyData(null);
            }}
            onSubmit={handleCreateChildCompany}
            formLoading={childCompanyFormLoading}
            formError={error}
            formSuccess={successMessage}
            parentCompanyData={parentCompanyData}
          />

          <AddDepartmentModal
            isOpen={showAddDepartmentModal}
            onClose={() => {
              setShowAddDepartmentModal(false);
            }}
            onSubmit={handleCreateDepartment}
            onAddChildCompany={(parentCompany: any) => {
              // Set the parent company data and show the AddChildCompanyModal
              setParentCompanyData(parentCompany);
              setShowAddChildCompanyModal(true);
            }}
            companies={companies}
            loadingCompanies={loadingCompanies}
            formLoading={departmentFormLoading}
            formError={error}
            formSuccess={successMessage}
            userRole={userRole}
          />
        </>
      )}
      {/* Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">Delete User</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(false)}
                ></button>
              </div>
              <div className="modal-body py-4">
                <p className="mb-0 fs-5 text-center text-secondary">
                  Are you sure you want to delete this user?
                  <br />
                  <small className="text-muted fs-6">This action cannot be undone.</small>
                </p>
              </div>
              <div className="modal-footer border-0 pt-0 justify-content-center pb-4">
                <button
                  type="button"
                  className="btn btn-light px-4 rounded-pill"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-4 rounded-pill"
                  onClick={confirmDeleteUser}
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-success">Success</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSuccessModal(false)}
                ></button>
              </div>
              <div className="modal-body py-4">
                <p className="mb-0 fs-5 text-center text-secondary">
                  {successMessage}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0 justify-content-center pb-4">
                <button
                  type="button"
                  className="btn btn-success px-4 rounded-pill"
                  onClick={() => setShowSuccessModal(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;